/**
 * `<ThreeCirclesPrivacyStrip/>` — unified, calm, factual surfacing of
 * the three-circles privacy model defined in
 * `docs/03_MULTI_USER_AND_PRIVACY.md` §3.
 *
 * Why this exists
 * ───────────────
 * Round-E verification audit #1 §3a.4 + Aisha (lead) UX log §7.2 +
 * Tom (member) UX log Dispatch 2 all converged on the same finding:
 * the three-circles model is *referenced* in cell-level tooltips
 * across the product (`TeamPage.tsx` "Visible to leads only", the
 * SubmitPage Circle-1 callout, the MeAudit privacy panel) but never
 * surfaced as a single 30-second readable strip. Per
 * `feedback_privacy_assurance_for_team_usage.md`: "the three-circles
 * model in `03` must be made visibly trustable on the homepage, in
 * product UI, and in marketing; worker adoption depends on it."
 *
 * Brand-pillar discipline (`feedback_brand_pillars.md`)
 * ─────────────────────────────────────────────────────
 * - Calmer noun: "what's visible to whom" rather than "tracking" or
 *   "monitoring." Workers reading this should feel reassured, not
 *   surveilled.
 * - "visible to" / "stored as" framing throughout; no audit-language.
 * - Calm tone token (border-border + bg-bg-subtle/40); never warning
 *   or danger.
 *
 * Focal-component discipline (`feedback_ui_section_focal_component.md`)
 * ─────────────────────────────────────────────────────────────────────
 * - On TeamPage Members tab: this strip is **supporting context, not
 *   focal**. The per-member table stays focal. The strip sits above
 *   the table as quiet reassurance before the lead types FTE / hours
 *   / rate.
 * - On the member-side first-entry interstitial (MeAudit first
 *   render): this strip IS focal. That's the entire point of the
 *   surface.
 * - `tone="focal"` lifts the visual weight (border-accent/30 +
 *   bg-accent-soft/40) for the focal placement; default `tone="quiet"`
 *   recedes.
 *
 * Variant — `audience: "lead" | "member"`
 * ───────────────────────────────────────
 * Per `docs/03_MULTI_USER_AND_PRIVACY.md` §3 the same three circles
 * apply universally; what differs is whose perspective the copy
 * speaks from:
 *   - `lead`:   emphasises Circle 2 visibility responsibility ("what
 *               you can see about each member"), framed for an
 *               authority who is about to enter contractual data on
 *               others' behalf.
 *   - `member`: emphasises Circle 1 ownership ("what stays yours")
 *               and the boundary against peers + the platform.
 *
 * Per-circle copy is verbatim-flagged for founder override on first
 * run; treat the strings below as v1 that's been brand-pillar-checked
 * but is not yet user-tested. Founder override expected during Round F+1
 * brand pass.
 *
 * Distillation note (per brief fallback rule)
 * ───────────────────────────────────────────
 * `docs/03_MULTI_USER_AND_PRIVACY.md` §3 is structurally clean
 * (Circle 1 / Circle 2 / Circle 3 with crisp bullet lists), so we
 * inline the canonical three-bullet structure faithfully — no
 * distillation drift. The strip is the readable surface; §3 remains
 * the canonical reference.
 */

import { Lock, ShieldCheck, Users } from "lucide-react";

import { cn } from "../lib/cn";

export interface ThreeCirclesPrivacyStripProps {
  /** Whose perspective the copy is framed from. */
  audience: "lead" | "member";
  /**
   * Visual weight.
   * - `quiet` (default): supporting-context placement above an
   *   existing focal element (TeamPage Members tab).
   * - `focal`: this strip is the section's focal element
   *   (member-side first-entry interstitial).
   */
  tone?: "quiet" | "focal";
  /**
   * Show the title line above the circles. Default `true`. The member
   * welcome page says the same words in its own subtitle, so it turns
   * the card's title off (#1949).
   */
  showTitle?: boolean;
  /** Optional className override. Token-driven; no raw hex expected. */
  className?: string;
}

interface CircleCopy {
  /** Header — circle name + scope keyword. */
  title: string;
  /** One-line plain-English description. */
  description: string;
  /** "Visible to: …" line that names the parties. */
  visibility: string;
}

interface AudienceCopy {
  intro: string;
  circles: [CircleCopy, CircleCopy, CircleCopy];
}

// ─── Per-audience copy (v1 — flagged for founder override) ────────────
//
// Each entry is shaped (title, description, visibility) so a worker
// reading the strip top-to-bottom sees: what the circle is, what it
// holds, who can see it. No "tracking" / "monitoring" tokens; no
// alarm-language.
const COPY: Record<"lead" | "member", AudienceCopy> = {
  lead: {
    intro: "How your team's data is stored on Tarii.",
    circles: [
      {
        title: "Circle 1 — private to each member",
        description:
          "Reasons attached to a leave request, free-text notes, and any individual slot ratings stay with the member who wrote them.",
        visibility: "Visible to: that member only.",
      },
      {
        title: "Circle 2 — visible to you as lead",
        description:
          "Member leave dates, contractual hours, FTE, eligibility, and rate (if your team uses pay tooling) so you can build a fair roster.",
        visibility: "Visible to: the member and team leads.",
      },
      {
        title: "Circle 3 — visible to the whole team",
        description:
          "The published roster grid and anonymised fairness summaries — solidarity numbers that never identify an individual.",
        visibility: "Visible to: every active team member.",
      },
    ],
  },
  member: {
    intro: "What stays yours, what your lead sees, what your team sees.",
    circles: [
      {
        title: "Circle 1 — yours alone",
        description:
          "Reasons behind your leave dates, anything you type into Notes, and your slot ratings stay with you.",
        visibility: "Visible to: you only.",
      },
      {
        title: "Circle 2 — you and your team lead",
        description:
          "Your leave dates, preferences, contractual hours, and FTE so your lead can build a fair roster — never your peers.",
        visibility: "Visible to: you and your team leads.",
      },
      {
        title: "Circle 3 — your whole team",
        description:
          "The published roster (who's on which day) and anonymised fairness summaries — never the reasons or notes behind them.",
        visibility: "Visible to: every active team member.",
      },
    ],
  },
};

const CIRCLE_ICONS = [Lock, ShieldCheck, Users] as const;

export function ThreeCirclesPrivacyStrip({
  audience,
  tone = "quiet",
  showTitle = true,
  className,
}: ThreeCirclesPrivacyStripProps) {
  const copy = COPY[audience];
  const isFocal = tone === "focal";

  return (
    <section
      aria-label="How your team's data is stored on Tarii"
      data-testid="three-circles-privacy-strip"
      data-audience={audience}
      data-tone={tone}
      className={cn(
        "flex flex-col gap-3 rounded-md border px-4 py-3",
        isFocal ? "border-accent/30 bg-accent-soft/40" : "border-border bg-bg-subtle/40",
        className,
      )}
    >
      <div className="flex flex-col gap-0.5">
        {showTitle ? (
          <p className={cn("font-semibold text-fg", isFocal ? "text-base" : "text-sm")}>
            {copy.intro}
          </p>
        ) : null}
        <p className="text-xs text-fg-muted">
          Three circles. Same model whether you're a lead or a member.
        </p>
      </div>

      <ul data-testid="three-circles-privacy-strip-circles" className="grid gap-3 sm:grid-cols-3">
        {copy.circles.map((circle, index) => {
          const Icon = CIRCLE_ICONS[index] ?? Lock;
          return (
            <li
              key={circle.title}
              data-testid={`three-circles-privacy-strip-circle-${index + 1}`}
              className={cn(
                "flex flex-col gap-2 rounded-md border px-3 py-2.5",
                isFocal ? "border-border bg-bg-elevated" : "border-border/60 bg-bg-elevated/60",
              )}
            >
              <div className="flex items-center gap-2">
                <Icon className="h-3.5 w-3.5 flex-shrink-0 text-fg-muted" aria-hidden="true" />
                <p className="text-xs font-semibold text-fg">{circle.title}</p>
              </div>
              <p className="text-xs text-fg-muted">{circle.description}</p>
              <p className="text-[11px] font-medium text-fg-subtle">{circle.visibility}</p>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
